import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { logout } from "../services/authService";
import {
  MyPackages,
  SubscriptionPackage,
  SubscriptionPayment,
  contactLink,
  formatMoney,
  getMyPackages,
  getPayment,
  listMyPayments,
} from "../services/subscriptionService";
import WhishPaymentDialog, {
  WHISH_DONE_MESSAGE,
  WHISH_WINDOW_NAME,
} from "../components/subscription/WhishPaymentDialog";
import { downloadSubscriptionInvoice } from "../utils/subscriptionInvoicePdf";
import { useSubscription } from "../contexts/SubscriptionContext";
import { useUser } from "../contexts/UserContext";
import PackageCard from "../components/subscription/PackageCard";
import { BRAND, LABELS } from "../platform/config";

const formatDate = (value: string) => format(new Date(value), "d MMMM yyyy");

const PAYMENT_STATUS: Record<
  SubscriptionPayment["status"],
  { label: string; color: "success" | "warning" | "error" }
> = {
  PAID: { label: "Paid", color: "success" },
  PENDING: { label: "Pending", color: "warning" },
  FAILED: { label: "Failed", color: "error" },
};

// This page loaded inside the Whish popup (Whish sent it back here): tell the
// payment window in the dashboard, then close.
const closeIfWhishPopup = (paymentId: string | null) => {
  if (!paymentId || window.name !== WHISH_WINDOW_NAME || !window.opener) return false;
  try {
    window.opener.postMessage({ type: WHISH_DONE_MESSAGE, paymentId }, window.location.origin);
  } catch {
    // The opener navigated away; it polls anyway.
  }
  window.close();
  return true;
};

// After Whish sends the owner back (popups blocked, so it was this tab), the
// payment is checked this often until Whish has confirmed it.
const POLL_MS = 3000;
const POLL_LIMIT = 20;

/**
 * The owner's subscription page: status, packages paid online with Whish,
 * and their invoices. Renewal is never automatic; the banner asks them to pay
 * 3 days before the end.
 *
 * `embedded`: rendered inside the backoffice layout like any other page (the
 * menu, the trial card and the banner link here). Without it this is the
 * stand-alone lock screen an expired organization is sent to, where the
 * layout's own calls would all be refused.
 */
const Subscription: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId?: string }>();
  const [params, setParams] = useSearchParams();
  const returnedPaymentId = params.get("payment");
  const [inPopup] = useState(() => closeIfWhishPopup(params.get("payment")));
  const { subscription, refreshSubscription } = useSubscription();
  const { user } = useUser();
  const isOwner = user?.role !== "EMPLOYEE";
  const [packages, setPackages] = useState<MyPackages | null>(null);
  const [payments, setPayments] = useState<SubscriptionPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingPkg, setPayingPkg] = useState<SubscriptionPackage | null>(null);
  // The payment the owner just came back from Whish with.
  const [returned, setReturned] = useState<SubscriptionPayment | null>(null);

  const loadPayments = useCallback(
    () => listMyPayments().then(setPayments).catch(() => undefined),
    []
  );

  useEffect(() => {
    // Re-read on open: the superadmin may have just renewed it.
    refreshSubscription();
    loadPayments();
    getMyPackages()
      .then(setPackages)
      .catch(() => setPackages(null))
      .finally(() => setLoading(false));
  }, [refreshSubscription, loadPayments]);

  // Back from Whish: wait for the payment to settle, then show the outcome.
  useEffect(() => {
    if (!returnedPaymentId || inPopup) return;
    let cancelled = false;
    let attempts = 0;
    const check = async () => {
      try {
        const payment = await getPayment(returnedPaymentId);
        if (cancelled) return;
        setReturned(payment);
        if (payment.status === "PENDING" && attempts < POLL_LIMIT) {
          attempts += 1;
          window.setTimeout(check, POLL_MS);
          return;
        }
        if (payment.status === "PAID") await refreshSubscription();
        loadPayments();
      } catch {
        // Unknown id (an old link): nothing to show.
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [returnedPaymentId, inPopup, refreshSubscription, loadPayments]);

  // Inside the dashboard, "back" is the clinic's home; the stand-alone lock
  // screen has no dashboard to go back to.
  const backToDashboard = () => {
    if (embedded && businessId) navigate(`/dashboard/${businessId}`);
    else if (!expiredRef.current) navigate("/home");
  };

  const dismissReturned = () => {
    setReturned(null);
    const next = new URLSearchParams(params);
    next.delete("payment");
    setParams(next, { replace: true });
  };

  const expired = subscription?.status === "EXPIRED";
  const expiredRef = React.useRef(expired);
  expiredRef.current = expired;
  const orgName = packages?.organizationName ?? "";
  const contact = packages?.contactWhatsapp ?? null;
  const demo = packages?.whishMode === "demo";
  // During a trial nothing has been paid for yet, so no card says "Renew".
  const currentPlanId = subscription?.isTrial ? undefined : subscription?.plan?.id;
  const canPay = isOwner && subscription?.status !== "UNLIMITED";

  const heading = (() => {
    if (!subscription) return { icon: ScheduleIcon, title: "Your subscription", body: "", tone: "info" };
    const days = subscription.daysLeft ?? 0;
    const dayText = `${days} day${days === 1 ? "" : "s"}`;
    switch (subscription.status) {
      case "EXPIRED":
        return {
          icon: LockClockOutlinedIcon,
          tone: "error",
          title: subscription.isTrial
            ? "Your free trial has ended"
            : "Your subscription has ended",
          body: `${LABELS.clients} can still book with you. Pay for a package below to reopen your backoffice; your data is safe and comes back exactly as you left it.`,
        };
      case "GRACE":
        return {
          icon: HourglassBottomIcon,
          tone: "error",
          title: `Payment due · ${dayText} before your dashboard locks`,
          body: `Your subscription ended on ${formatDate(subscription.endsAt!)}. Pay for a package to keep using the system; the new period starts from that date.`,
        };
      case "TRIAL":
        return {
          icon: ScheduleIcon,
          tone: "info",
          title: `Free trial · ${dayText} left`,
          body: `Your trial runs until ${formatDate(subscription.endsAt!)}. Pay for a package any time; it starts when the trial ends, so you lose no trial days.`,
        };
      case "EXPIRING":
      case "ACTIVE":
        return {
          icon: VerifiedOutlinedIcon,
          tone: "info",
          title: subscription.plan
            ? `${subscription.plan.name} · ${dayText} left`
            : `Active · ${dayText} left`,
          body: `Your subscription runs until ${formatDate(subscription.endsAt!)}. It does not renew by itself: pay again before then and the new period is added on top.`,
        };
      default:
        return {
          icon: VerifiedOutlinedIcon,
          tone: "info",
          title: "Your account has no end date",
          body: "Nothing to pay. The packages below are here for reference.",
        };
    }
  })();
  const HeadingIcon = heading.icon;
  const alarming = heading.tone === "error";

  const body = (
    <>
      {returned && (
        <Alert
          severity={
            returned.status === "PAID" ? "success" : returned.status === "FAILED" ? "error" : "info"
          }
          icon={returned.status === "PENDING" ? <CircularProgress size={20} /> : undefined}
          onClose={returned.status === "PENDING" ? undefined : dismissReturned}
          sx={{ mb: 3 }}
        >
          {returned.status === "PAID" &&
            `Payment received. ${returned.planName} is active until ${formatDate(
              returned.periodEndsAt!
            )}. Invoice #${returned.invoiceNumber}.`}
          {returned.status === "FAILED" &&
            "The payment did not go through, so nothing was charged. You can try again."}
          {returned.status === "PENDING" && "Waiting for Whish to confirm your payment…"}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: alarming ? "#F0C2BD" : "divider",
          bgcolor: alarming ? "#FDF3F2" : "background.paper",
          borderRadius: 3,
          p: { xs: 2.5, sm: 3.5 },
          mb: 4,
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <HeadingIcon
          aria-hidden
          sx={{ fontSize: 32, color: alarming ? "#A32B22" : "primary.main", mt: 0.25 }}
        />
        <Box>
          <Typography
            component="h1"
            sx={{ fontWeight: 700, fontSize: { xs: "1.35rem", sm: "1.6rem" }, lineHeight: 1.3 }}
          >
            {heading.title}
          </Typography>
          {heading.body && (
            <Typography sx={{ mt: 0.75, color: "text.secondary", maxWidth: 680, lineHeight: 1.6 }}>
              {heading.body}
            </Typography>
          )}
        </Box>
      </Paper>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
          Packages
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
          Paid with Whish: enter your number on the Whish page and confirm with the code in your
          Whish app.
        </Typography>
      </Box>

      {demo && canPay && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Whish is in demo mode on this server: payments are simulated and no money moves.
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && (!packages || packages.packages.length === 0) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Packages are being prepared.{" "}
          {contact ? "Message us and we'll set you up." : "Please contact your account manager."}
        </Alert>
      )}

      {!loading && packages && packages.packages.length > 0 && (
        <Box
          data-tour="packages"
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: `repeat(${Math.min(packages.packages.length, 3)}, 1fr)`,
            },
            mb: 3,
          }}
        >
          {packages.packages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              highlighted={pkg.id === currentPlanId}
              footer={
                canPay ? (
                  <Button
                    variant={pkg.id === currentPlanId || !currentPlanId ? "contained" : "outlined"}
                    disableElevation
                    disabled={Boolean(payingPkg) || returned?.status === "PENDING"}
                    onClick={() => setPayingPkg(pkg)}
                    fullWidth
                    sx={{ textTransform: "none", fontWeight: 700, minHeight: 44 }}
                  >
                    {pkg.id === currentPlanId ? "Renew with Whish" : "Pay with Whish"}
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                    {isOwner ? "Nothing to pay on this account." : "The account owner pays for the subscription."}
                  </Typography>
                )
              }
            />
          ))}
        </Box>
      )}

      {payments.length > 0 && (
        <Box component="section" aria-labelledby="invoices-heading" sx={{ mb: 3 }}>
          <Typography id="invoices-heading" component="h2" sx={{ fontWeight: 700, fontSize: "1.2rem", mb: 1.5 }}>
            Payments &amp; invoices
          </Typography>
          <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
            {payments.map((payment, index) => (
              <Box key={payment.id}>
                {index > 0 && <Divider />}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, flexWrap: "wrap" }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Invoice #{payment.invoiceNumber} · {payment.planName}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                      {format(new Date(payment.paidAt ?? payment.createdAt), "d MMM yyyy")}
                      {payment.periodStartsAt && payment.periodEndsAt
                        ? ` · ${format(new Date(payment.periodStartsAt), "d MMM")} – ${format(
                            new Date(payment.periodEndsAt),
                            "d MMM yyyy"
                          )}`
                        : ""}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {formatMoney(payment.amount, payment.currency)}
                  </Typography>
                  <Chip
                    size="small"
                    label={PAYMENT_STATUS[payment.status].label}
                    color={PAYMENT_STATUS[payment.status].color}
                    variant="outlined"
                  />
                  {payment.status === "PAID" && (
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadSubscriptionInvoice(payment, orgName)}
                      sx={{ textTransform: "none", minHeight: 36 }}
                    >
                      Invoice
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      {contact && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            color: "text.secondary",
          }}
        >
          <Typography sx={{ fontSize: "0.9rem", flexGrow: 1 }}>
            Questions, or need something custom? We reply on WhatsApp.
          </Typography>
          <Button
            startIcon={<WhatsAppIcon />}
            href={contactLink(contact, orgName)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }}
          >
            Contact us
          </Button>
        </Box>
      )}
    </>
  );

  const paymentWindow = (
    <WhishPaymentDialog
      pkg={payingPkg}
      organizationName={orgName}
      mode={packages?.whishMode ?? "demo"}
      onPaid={() => {
        refreshSubscription();
        loadPayments();
      }}
      onAbandoned={() => {
        setPayingPkg(null);
        loadPayments();
        toast.info("Payment cancelled. Nothing was charged.", { position: "top-right" });
        backToDashboard();
      }}
      onDismiss={() => setPayingPkg(null)}
      onDone={() => {
        setPayingPkg(null);
        backToDashboard();
      }}
    />
  );

  // Only the Whish popup's return trip lands here; it is closing itself.
  if (inPopup) return null;

  if (embedded)
    return (
      <Box sx={{ pt: 1 }}>
        {body}
        {paymentWindow}
      </Box>
    );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700 }} noWrap>
            {BRAND.name}
          </Typography>
          {orgName && (
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }} noWrap>
              {orgName}
            </Typography>
          )}
        </Box>
        {!expired && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/home")}
            sx={{ textTransform: "none", minHeight: 44 }}
          >
            Back
          </Button>
        )}
        <Button
          startIcon={<LogoutIcon />}
          onClick={() => logout()}
          color="inherit"
          sx={{ textTransform: "none", minHeight: 44 }}
        >
          Log out
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        {body}
      </Container>
      {paymentWindow}
    </Box>
  );
};

export default Subscription;
